import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  CodeBracketIcon,
  DevicePhoneMobileIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import React from "react";

import { StudioIntent } from "utils/easyaiStudio";

interface EasyAIStudioCardProps {
  onLaunch: (intent: StudioIntent) => void;
  /** Intent currently being launched, so only that button shows a spinner. */
  launchingIntent: StudioIntent | null;
  /** Whether we have a license key to hand to the Studio. */
  hasLicenseKey: boolean;
}

type StudioChoice = {
  intent: StudioIntent;
  label: string;
  tagline: string;
  bullets: string[];
  icon: typeof CodeBracketIcon;
};

/**
 * Two choices, split by what the customer is integrating against. The Studio's
 * chat-vs-build modes are deliberately not surfaced here: the wizard asks, and
 * making it a second top-level axis just doubled the buttons without telling
 * anyone what they were picking between.
 */
const CHOICES: StudioChoice[] = [
  {
    intent: "nutritionApi",
    label: "Nutrition REST API",
    tagline: "Server-side and web integrations",
    bullets: [
      "Knows every route, header and payload shape",
      "Recommends the right route for your use case",
      "Builds and hosts a working app against your account",
    ],
    icon: CodeBracketIcon,
  },
  {
    intent: "mobileSdk",
    label: "Mobile SDKs",
    tagline: "iOS, Android, React Native, Flutter",
    bullets: [
      "Real class and method names per platform",
      "Proxy mode and the v2 to v3 migration",
      "Ask questions or scaffold an integration",
    ],
    icon: DevicePhoneMobileIcon,
  },
];

const EasyAIStudioCard: React.FC<EasyAIStudioCardProps> = ({
  onLaunch,
  launchingIntent,
  hasLicenseKey,
}) => {
  const busy = launchingIntent !== null;

  return (
    <div className="overflow-hidden rounded-md border border-indigo-200 shadow-lg">
      {/* --- Headline band. On-brand lime ramp rather than the indigo-to-lime
          blend it replaced, which read as a blue-green smear against the rest
          of the portal. --- */}
      <div className="relative bg-gradient-to-br from-[#1a2e05] via-[#365314] to-[#4d7c0f] px-6 py-6 sm:px-8">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#bef264]/10 blur-3xl"
        />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-sm font-bold uppercase tracking-wide text-white">
            <SparklesIcon className="h-3.5 w-3.5" />
            EasyAI Studio
            <span className="ml-1 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 px-1.5 py-0.5 text-[10px] leading-none">
              NEW
            </span>
          </span>
          <h3 className="mt-3 text-xl font-semibold text-white sm:text-2xl">
            Let the Studio build your integration for you
          </h3>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#ecfccb]">
            Describe what you want in plain English and the Studio writes, hosts
            and runs it against your own account. Pick what you are integrating
            against — it will ask whether you would rather ask questions or have
            it build something.
          </p>
        </div>
      </div>

      {/* --- The two choices --- */}
      <div className="grid gap-px bg-gray-200 md:grid-cols-2">
        {CHOICES.map(({ intent, label, tagline, bullets, icon: Icon }) => (
          <div key={intent} className="flex flex-col bg-white p-5">
            <div className="flex items-center gap-3">
              <span className="flex-shrink-0 rounded-lg bg-[#f7fee7] p-2 text-indigo-700">
                <Icon className="h-6 w-6" />
              </span>
              <div className="min-w-0">
                <p className="text-md font-semibold text-gray-900">{label}</p>
                <p className="text-sm text-gray-500">{tagline}</p>
              </div>
            </div>
            <ul className="mt-3 flex-1 space-y-1.5">
              {bullets.map((bullet) => (
                <li
                  key={bullet}
                  className="flex gap-2 text-sm leading-relaxed text-gray-600"
                >
                  <span
                    aria-hidden="true"
                    className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-400"
                  />
                  {bullet}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => onLaunch(intent)}
              disabled={busy}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#4d7c0f] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#3f6212] disabled:cursor-wait disabled:opacity-70"
            >
              {launchingIntent === intent ? (
                <span
                  aria-hidden="true"
                  className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white"
                />
              ) : (
                <Icon className="h-5 w-5" />
              )}
              Open the {label} assistant
              <ArrowTopRightOnSquareIcon className="h-4 w-4 opacity-70" />
            </button>
          </div>
        ))}
      </div>

      {/* --- Footer: the existing-app entry, plus what happens to the key.
          Deliberately says "paste into the license step" rather than claiming
          the key lets the Studio call the API: that holds on the REST path, but
          in SDK mode nothing server-side consumes it and generated code ships a
          YOUR_PASSIO_KEY placeholder. --- */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-5">
        <button
          type="button"
          onClick={() => onLaunch("existingApp")}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-700 transition-colors hover:text-indigo-800 disabled:cursor-wait disabled:opacity-70"
        >
          {launchingIntent === "existingApp" ? (
            <span
              aria-hidden="true"
              className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-700"
            />
          ) : null}
          Open an app you already started
          <ArrowRightIcon className="h-4 w-4" />
        </button>
        {hasLicenseKey && (
          <p className="text-sm text-gray-500">
            Your API key is copied on launch, ready to paste into the
            Studio&apos;s license step.
          </p>
        )}
      </div>
    </div>
  );
};

export default EasyAIStudioCard;
